import { TRADING_VIEW_LANG_MAP } from '@/constants/TradingViewLangMap'
import type { DatafeedConfiguration, IBasicDataFeed, IDatafeedQuotesApi, LanguageCode, LibrarySymbolInfo, ResolutionString, Timezone } from 'charting-library'
import { HistoryKlineResolutionDiffMap, ResolutionsMap, SvIds, useEnvStore, useKlineStore, useLanguageStore, useStaticTableDataStore } from 'store'
import { useChangeResolution, useChangeSymbol } from 'store/src/hooks'
import { safeMillisecondToSecond } from 'utils'
import { useTradingViewStreaming } from './use-trading-view-streaming'

type WidgetDatafeed = IBasicDataFeed | (IBasicDataFeed & IDatafeedQuotesApi)

type CreateDatafeedParams = {
  symbol: SymbolId
}

export const useTradingViewDatafeed = () => {
  const klineStore = useKlineStore()
  const streaming = useTradingViewStreaming()
  const envStore = useEnvStore()
  const languageStore = useLanguageStore()
  const staticTableStore = useStaticTableDataStore()

  const resolveSymbol = (pair: string) => {
    if (pair) {
      pair = pair.toLowerCase()
    }
    return pair.replace(/\//, '_')
  }

  const getLocale = (): LanguageCode => {
    const lang = TRADING_VIEW_LANG_MAP[languageStore.currentLanguage]
    return lang
  }

  const createDatafeed = async (data: CreateDatafeedParams): Promise<WidgetDatafeed> => {
    await envStore.waitForConfig()

    if (!staticTableStore.staticTableDataMap[SvIds.StaticSymbol]) {
      await staticTableStore.fetchStaticTableData([SvIds.StaticSymbol])
    }

    let staticSymbolInfo = staticTableStore.staticTableDataMap[SvIds.StaticSymbol][data.symbol]
    if (!staticSymbolInfo) {
      await staticTableStore.fetchStaticTableData([SvIds.StaticSymbol])
      staticSymbolInfo = staticTableStore.staticTableDataMap[SvIds.StaticSymbol][data.symbol]
    }

    const klineSymbolInfo = {
      supportedResolutions: ['1', '5', '15', '30', '60', '240', '1D', '1W', '1M'] as ResolutionString[],
    }
    return {
      onReady: (cb) => {
        const config: DatafeedConfiguration = {
          supported_resolutions: klineSymbolInfo.supportedResolutions as ResolutionString[],
        }
        Promise.resolve().then(() => {
          cb(config)
        })
      },
      getBars: async (symbolInfo, resolution, periodParams, onHistoryCallback) => {
        const { to, firstDataRequest, from } = periodParams
        const symbol = resolveSymbol(symbolInfo.name)

        const diffTime = HistoryKlineResolutionDiffMap[resolution as keyof typeof HistoryKlineResolutionDiffMap]
        const resolutionCount = 1000 // 周期数量，diffTime是一个周期的秒数
        const req = {
          symbol: symbol,
          resolution: ResolutionsMap[resolution as keyof typeof ResolutionsMap],
          from: from,
          to: to,
          size: resolutionCount,
        }
        const historyKlineList = await klineStore.getHistoryKline(req)

        if (historyKlineList.length) {
          const lastHistoryKline = historyKlineList[historyKlineList.length - 1]
          if (firstDataRequest) {
            streaming.setLastHistoryKline(lastHistoryKline)
          }

          // 下一次请求结束的时间，必须是上一次开始时间减去一个K线的时间
          klineStore.setToTime(safeMillisecondToSecond(historyKlineList[0].time) - diffTime)
          onHistoryCallback(historyKlineList, {
            noData: false,
          })
        } else {
          onHistoryCallback(historyKlineList, {
            noData: true,
          })
        }
      },

      searchSymbols: async () => {},

      resolveSymbol: async (symbolName, onResolve) => {
        const symbolInfo: LibrarySymbolInfo = {
          name: symbolName,
          type: 'crypto',
          session: '24x7',
          minmov: 1,
          minmove2: 0,
          pricescale: 10 ** (staticSymbolInfo?.pricePrecision ?? 1),
          timezone: 'Asia/Shanghai' as Timezone,
          full_name: staticSymbolInfo?.logicName.replace('_', '/').toUpperCase(),
          description: '',
          exchange: '',
          supported_resolutions: klineSymbolInfo.supportedResolutions as ResolutionString[],
          listed_exchange: '',
          format: 'price',
          has_weekly_and_monthly: true,
          has_empty_bars: true,
          has_intraday: true,
        }
        Promise.resolve().then(() => {
          onResolve(symbolInfo)
        })
      },

      subscribeBars: async (symbol, resolution, onRealtimeCallback, subscriberUID) => {
        streaming.subscribeBars(symbol, resolution, onRealtimeCallback, subscriberUID)
      },

      unsubscribeBars: async (subscriberUID) => {
        streaming.unsubscribeBars(subscriberUID)
      },
    }
  }

  useChangeSymbol(() => {
    klineStore.setToTime(0)
  })

  useChangeResolution((_, oldResolution) => {
    klineStore.setToTime(0)
    klineStore.unsubscribeKlineWithResolution(oldResolution)
  })

  onBeforeUnmount(() => {
    klineStore.$dispose()
  })

  return {
    createDatafeed,
    getLocale,
  }
}
